import React, { useEffect, useRef, useState } from 'react';
import styles from './CircularSearchBar.module.scss';
import { useAppSelector } from '../../../../app/hooks';
import clsx from 'clsx';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';

interface CircularSearchBarProps {
    placeholder?: string;
    options: {
        orgName: string;
        assesseeOrgId: string;
    }[];
    onChange: (value: string) => void;
    onSelect: (value: { orgName: string; assesseeOrgId: string }) => Promise<boolean>;
    showList?: boolean;
    onOpenAddLead: (prefill: string) => void;
    onFocus: () => void;
    isSelectingOrg?: boolean;
}

const CircularSearchBar: React.FC<CircularSearchBarProps> = ({
    placeholder = 'Search...',
    options,
    onChange,
    onSelect,
    showList,
    onOpenAddLead,
    onFocus,
    isSelectingOrg,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Fallback to storage if Redux isn't hydrated yet (prevents 401 on page refresh)
    const investorOrgIdFromRedux = useAppSelector((state) => state.user.userData.investorOrgId);
    const investorOrgId = investorOrgIdFromRedux || sessionStorage.getItem('org_id') || localStorage.getItem('org_id') || '';

    const [filteredOptions, setFilteredOptions] = useState<
        {
            orgName: string;
            assesseeOrgId: string;
            disabled?: true;
        }[]
    >([]);
    const dropdownRef = useRef(null);
    const [isSearching, setIsSearching] = useState(false);

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !(dropdownRef.current as any).contains(event.target)) {
            setSearchTerm('');
            // Do not call onChange here; parent filters stay intact to avoid wiping prefill for the modal.
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showList && searchTerm.length > 3) {
            if (options.length === 0) {
                setFilteredOptions([
                    {
                        orgName: 'No Results Found',
                        assesseeOrgId: '',
                        disabled: true,
                    },
                ]);
            } else {
                setFilteredOptions(options);
            }
        }
    }, [options]);

    const handleChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        onChange(value);

        if (value.length > 3) {
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
    };
    useEffect(() => {
        if (showList && searchTerm?.length > 3) {
            setIsSearching(false);
        }
    }, [options]);

    return (
        <div ref={dropdownRef} style={{ position: 'relative' }}>
            <div className={styles.searchBar}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onFocus={(e) => {
                        e.stopPropagation();
                        if (!showList) {
                            setSearchTerm('');
                            onChange('');
                            onFocus();
                        }
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    className={styles.input}
                />
                {isSearching || isSelectingOrg ? (
                    <LoadingSpinner color="#df666a" style={{ width: '0.75rem', height: '0.75rem', marginRight: '0.5rem' }} />
                ) : (
                    <div className={styles.searchIcon} />
                )}
            </div>
            {showList && searchTerm.length > 3 && (
                <div className={styles.dropdown}>
                    {filteredOptions.map((option, index) => (
                        <div
                            onMouseDown={async (e) => {
                                if (option.disabled) return;
                                e.stopPropagation();
                                setSearchTerm('');
                                const res = await onSelect(option);
                            }}
                            key={index}
                            className={clsx({
                                [styles.option]: true,
                                [styles.optionDisabled]: option.disabled,
                            })}
                        >
                            {option.orgName}
                        </div>
                    ))}
                    <div
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            onOpenAddLead(searchTerm);
                        }}
                        className={styles.addNewLead}
                    >
                        <span className={styles.addIcon}>+</span> Add New Lead
                    </div>
                </div>
            )}
        </div>
    );
};

export default CircularSearchBar;
